import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Clock,
  FileText,
  CheckCircle,
  AlertTriangle,
  ArrowLeft,
  Play,
  Loader2,
} from 'lucide-react'
import { useStartTestAttempt } from '../hooks/useMockTest'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useCompleteAssessment } from '@/features/test-series/hooks'
import {
  trackTestStart,
  trackError,
  trackQuickTestStart,
  trackFullTestStart,
} from '@/shared/utils/analytics'
import { fbTrackTestStart, fbTrackViewContent } from '@/shared/utils/facebookPixel'
import { FREE_TIER_LIMITS } from '@/features/payments/utils/constants'
import { useSolutionAccess } from '@/features/solution-browser/hooks/useActivationKey'
import ActivationKeyGate from '@/features/solution-browser/components/ActivationKeyGate'
import type { PaperbackSubject } from '@/features/solution-browser/types'

const PAPERBACK_SUBJECTS: PaperbackSubject[] = ['PHYSICS', 'CHEMISTRY', 'BIOLOGY']

export default function TestInstructionsPage() {
  const navigate = useNavigate()
  const { testId } = useParams<{ testId: string }>()
  const [agreedToTerms, setAgreedToTerms] = useState(false)

  const { user } = useAuthStore()
  const userId = useAuthStore((s) => s.userId) ?? undefined
  const queryClient = useQueryClient()
  const startAttempt = useStartTestAttempt()

  // Fetch complete assessment details (with sections)
  const { data: assessmentData, isLoading, error } = useCompleteAssessment(testId || '')
  const assessment = assessmentData?.data

  // Activation key gate: check if this test requires access
  const isFreeTest = assessment?.testCode
    ? FREE_TIER_LIMITS.allowedTestCodes.includes(assessment.testCode)
    : false

  const { data: accessData, isLoading: accessLoading } = useSolutionAccess(userId)

  const testSubjects: PaperbackSubject[] = assessment?.sections
    ? ([
        ...new Set(
          assessment.sections
            .map((s) => s.sectionType)
            .filter((t): t is PaperbackSubject => PAPERBACK_SUBJECTS.includes(t as PaperbackSubject))
        ),
      ] as PaperbackSubject[])
    : []

  const unlockedSubjects = accessData?.subjects ?? []
  const hasAccess =
    isFreeTest ||
    !assessment ||
    testSubjects.length === 0 ||
    testSubjects.some((s) => unlockedSubjects.includes(s))

  // Track ViewContent for FB Pixel
  useEffect(() => {
    if (assessment) {
      fbTrackViewContent('test_instructions', assessment.testCode, `Test: ${assessment.testName}`)
    }
  }, [assessment])

  const handleStartTest = async () => {
    const studentId = user?.email || user?.phoneNumber
    if (!agreedToTerms || !studentId || !testId || !assessment) {
      console.error('Cannot start test:', { agreedToTerms, studentId, testId, assessment: !!assessment })
      return
    }

    try {
      const result = await startAttempt.mutateAsync({
        studentId,
        testCode: assessment.testCode, // Use testCode (business key)
      })

      if (result.success && result.data?.attemptId) {
        // Track test start event
        trackTestStart(assessment.testCode, assessment.testName)
        fbTrackTestStart(assessment.testCode, assessment.testName)

        // Track specific test type events
        if (assessment.testType === 'QUICK_DIAGNOSTIC') {
          trackQuickTestStart()
        } else if (assessment.testType === 'FULL_LENGTH') {
          trackFullTestStart()
        }

        navigate(`/mock-test/${testId}/attempt/${result.data.attemptId}`)
      } else {
        console.error('Start test response missing attemptId:', result)
      }
    } catch (error) {
      console.error('Failed to start test:', error)
      trackError('test_start', error instanceof Error ? error.message : 'Failed to start test', assessment?.testCode)
    }
  }

  // Check if we can start the test
  const canStartTest = agreedToTerms && !!(user?.email || user?.phoneNumber) && !!testId && !!assessment

  const handleGoBack = () => {
    navigate('/dashboard')
  }

  // Calculate marks per question (assuming equal distribution, default to 4 for NEET)
  const marksPerQuestion = assessment && assessment.totalQuestions > 0
    ? Math.round(assessment.totalMarks / assessment.totalQuestions)
    : 4

  // Loading state
  if (isLoading || (!isFreeTest && accessLoading && !!userId)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading test details...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !assessment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Test</h2>
          <p className="text-gray-600 mb-4">
            {error instanceof Error ? error.message : 'Unable to load test details. Please try again.'}
          </p>
          <button
            onClick={handleGoBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  if (!hasAccess && userId) {
    return (
      <ActivationKeyGate
        userId={userId}
        testSubjects={testSubjects}
        testName={assessment?.testName}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['solution-access', userId] })}
      />
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button
            onClick={handleGoBack}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{assessment.testName}</h1>
            <p className="text-sm text-gray-500">Test Instructions</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Test Overview */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Overview</h2>

          {assessment.description && (
            <div
              className="text-gray-600 mb-4 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: assessment.description }}
            />
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Clock className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-blue-600">{assessment.durationMinutes}</div>
              <div className="text-xs text-gray-600">Minutes</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <FileText className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-green-600">{assessment.totalQuestions}</div>
              <div className="text-xs text-gray-600">Questions</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-purple-600">{assessment.totalMarks}</div>
              <div className="text-xs text-gray-600">Total Marks</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-orange-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-orange-600">-{assessment.negativeMarkingRatio}</div>
              <div className="text-xs text-gray-600">Negative Marks</div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Passing Marks</p>
              <p className="text-lg font-semibold text-gray-900">{assessment.passingMarks}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Test Type</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">
                {assessment.testType.replace(/_/g, ' ').toLowerCase()}
              </p>
            </div>
          </div>

          {/* Section Breakdown */}
          {assessment.sections && assessment.sections.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Section Breakdown</h3>
              <div className="space-y-2">
                {assessment.sections.map((section) => (
                  <div
                    key={section.sectionId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="font-medium text-gray-700">{section.sectionName}</span>
                    <div className="flex gap-4 text-sm text-gray-500">
                      <span>{section.sectionQuestions} Qs</span>
                      <span>{section.sectionMarks} marks</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Instructions</h2>

          <div className="space-y-4 text-gray-600">
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 text-sm font-medium rounded-full">1</span>
              <p>The test contains <strong>{assessment.totalQuestions}</strong> questions. You have <strong>{assessment.durationMinutes} minutes</strong> to complete the test.</p>
            </div>

            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 text-sm font-medium rounded-full">2</span>
              <p>Each correct answer carries <strong>+{marksPerQuestion} marks</strong>. Each incorrect answer carries <strong>-1 mark</strong>. Unanswered questions carry <strong>0 marks</strong>.</p>
            </div>

            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 text-sm font-medium rounded-full">3</span>
              <p>You can navigate between questions using the <strong>Previous</strong> and <strong>Next</strong> buttons or by clicking on the question number in the question palette.</p>
            </div>

            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 text-sm font-medium rounded-full">4</span>
              <p>You can mark questions for review and revisit them later. Use the <strong>Mark for Review</strong> button to flag a question.</p>
            </div>

            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 text-sm font-medium rounded-full">5</span>
              <p>The timer at the top shows the remaining time. The test will be auto-submitted when time runs out.</p>
            </div>

            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 text-sm font-medium rounded-full">6</span>
              <p>Once you submit the test, you <strong>cannot modify</strong> your answers. Make sure to review all questions before submitting.</p>
            </div>

            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-yellow-100 text-yellow-600 text-sm font-medium rounded-full">!</span>
              <p className="text-yellow-700"><strong>Important:</strong> Do not refresh the page or close the browser during the test. Your progress is saved automatically.</p>
            </div>
          </div>
        </div>

        {/* Agreement & Start */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">
              I have read and understood the instructions. I agree to follow the rules and understand that any malpractice may lead to disqualification.
            </span>
          </label>

          <button
            onClick={handleStartTest}
            disabled={!canStartTest || startAttempt.isPending}
            className="w-full mt-6 flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {startAttempt.isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Starting Test...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Start Test
              </>
            )}
          </button>

          {!user && (
            <p className="mt-3 text-sm text-orange-600 text-center">
              Please log in to start the test.
            </p>
          )}

          {startAttempt.isError && (
            <p className="mt-3 text-sm text-red-600 text-center">
              Failed to start test. Please try again.
            </p>
          )}
        </div>
      </main>
    </div>
  )
}
